// pattern: Functional Core (pure async — side effects isolated to canvas + pdfjs)

/**
 * PDF Page Renderer — renders specific PDF pages as JPEG data URLs
 * Uses pdfjs-dist (lazy-loaded, Vite code-splits automatically)
 */

export type RenderOptions = {
  readonly targetWidth?: number;
  readonly jpegQuality?: number;
};

export type RenderResult = {
  readonly rendered: ReadonlyMap<number, string>;
  readonly errors: ReadonlyArray<string>;
};

const DEFAULT_TARGET_WIDTH = 800;
const DEFAULT_JPEG_QUALITY = 0.7;

export async function renderPdfPages(
  pdfData: ArrayBuffer,
  pageNumbers: ReadonlyArray<number>,
  options?: RenderOptions
): Promise<RenderResult> {
  const targetWidth = options?.targetWidth ?? DEFAULT_TARGET_WIDTH;
  const jpegQuality = options?.jpegQuality ?? DEFAULT_JPEG_QUALITY;

  // Deduplicate page numbers
  const uniquePages = [...new Set(pageNumbers)];

  if (uniquePages.length === 0) {
    return { rendered: new Map(), errors: [] };
  }

  const pdfjs = await import('pdfjs-dist');

  const rendered = new Map<number, string>();
  const errors: string[] = [];

  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data: new Uint8Array(pdfData) }).promise;
  } catch {
    return { rendered: new Map(), errors: ['Failed to load PDF document'] };
  }

  for (const pageNum of uniquePages) {
    if (pageNum < 1 || pageNum > pdf.numPages) {
      errors.push(`Page ${pageNum} out of range (document has ${pdf.numPages} pages)`);
      continue;
    }

    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const scale = targetWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      await page.render({ canvas, viewport: scaledViewport }).promise;

      const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
      rendered.set(pageNum, dataUrl);
    } catch {
      errors.push(`Failed to render page ${pageNum}`);
    }
  }

  return { rendered, errors };
}
